import { useCallback, useRef, useState } from 'react';
import { Cookies } from 'react-cookie';
import { Logger } from '@saviynt/common';

import { CHAT_BLOCK } from '../constants';
import { type ChatMessage, type ChatMode, type MarkdownBlock } from '../types';
import { checkEnvironment } from '../utils';

const cookies = new Cookies();

const useGetAssitantReply = () => {
  const abortRef = useRef<AbortController | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleAssistant = useCallback(
    async (
      queryBlock: ChatMessage<ChatMode>,
      onChunk: (chunk: ChatMessage<ChatMode>) => void
    ) => {
      setIsLoading(true);
      setIsError(false);

      abortRef.current = new AbortController();

      try {
        const { GATEWAY_API_URL } = checkEnvironment();
        const token = cookies.get<string>('access_token');

        const response = await fetch(
          `${GATEWAY_API_URL ?? ''}/api/aiplatform/api/v1/agent/chat`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` }),
            },
            body: JSON.stringify(
              queryBlock.blocks[0].type === CHAT_BLOCK.MARKDOWN
                ? {
                    query: (queryBlock.blocks[0] as MarkdownBlock).content,
                    session_id: queryBlock.id,
                  }
                : {
                    query: queryBlock,
                    session_id: queryBlock.id,
                  }
            ),
            // body: JSON.stringify(queryBlock),
            signal: abortRef.current.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        let buffer = '';
        const decoder = new TextDecoder('utf-8');
        const reader = response.body?.getReader();

        if (!reader) {
          return;
        }

        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, no-constant-condition
        while (true) {
          // eslint-disable-next-line no-await-in-loop
          const { value, done } = await reader.read();

          if (done) {
            // flush last incomplete piece
            buffer += decoder.decode();
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');

          // keep the last line in buffer if incomplete
          buffer = lines.pop() ?? '';

          // eslint-disable-next-line no-restricted-syntax
          for (const line of lines) {
            if (!line) {
              // eslint-disable-next-line no-continue
              continue;
            }

            if (line.startsWith('data: ')) {
              try {
                const json = JSON.parse(line.slice(6)) as ChatMessage<ChatMode>;

                onChunk(json);
              } catch {
                Logger.log('Malformed JSON', line);
              }
            }
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          throw new Error('AbortError');
        }

        setIsError(true);

        throw new Error(
          'System encountered an error. Please contact the Support Administrator'
        );
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const stopCall = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return {
    handleAssistant,
    stopCall,
    isError,
    isLoading,
  };
};

// eslint-disable-next-line import/prefer-default-export
export { useGetAssitantReply };
